import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticSolarInverter } from "./realistic";

/**
 * SolarInverterNode – Solar inverter visualization.
 *
 * Supports:
 * - Realistic 3D SCADA design with backlit LCD screen & 3 indicator LEDs
 * - Premium glassmorphism layout
 * - Classic industrial SVG layout
 */
const SolarInverterNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, ratio, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticSolarInverter
              liveValue={liveValue}
              unit={unit || "kW"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              ratio={ratio}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const isActive = isRunning;
        const dotColor =
          statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

        // AC wave and pulse animation speed
        const waveDuration = isActive ? `${Math.max(0.4, 1.5 / (speedScale || 1))}s` : "0s";
        const pulseDuration = isActive ? `${Math.max(0.4, 1.2 / (speedScale || 1))}s` : "0s";
        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="70"
            height="78"
            viewBox="0 0 70 78"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  {/* Inverter chassis gradient */}
                  <linearGradient id="invChassis" x1="0" y1="0" x2="70" y2="70">
                    <stop offset="0%" stopColor="#f8fafc" />
                    <stop offset="60%" stopColor="#e2e8f0" />
                    <stop offset="100%" stopColor="#cbd5e1" />
                  </linearGradient>
                  {/* LCD screen gradient */}
                  <linearGradient id="lcdScreen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0f172a" />
                    <stop offset="100%" stopColor="#1e293b" />
                  </linearGradient>
                  <filter id="lcdGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="1.5" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Heat sink fins on left/right sides */}
                <rect x="6" y="10" width="2" height="42" fill="#475569" rx="0.5" />
                <rect x="4" y="14" width="2" height="34" fill="#475569" rx="0.5" />
                <rect x="62" y="10" width="2" height="42" fill="#475569" rx="0.5" />
                <rect x="64" y="14" width="2" height="34" fill="#475569" rx="0.5" />

                {/* Main Inverter Enclosure */}
                <rect x="8" y="6" width="54" height="50" rx="4" fill="url(#invChassis)" stroke="#94a3b8" strokeWidth="1.5" />

                {/* Branding line or design line */}
                <line x1="8" y1="16" x2="62" y2="16" stroke="#94a3b8" strokeWidth="1" />
                <text x="14" y="12" fontSize="5.5" fontWeight="bold" fill="#64748b">SOLAR INVERTER</text>

                {/* Digital LCD Display (Circular and glowing when active) */}
                <circle cx="35" cy="32" r="16" fill="url(#lcdScreen)" stroke="#475569" strokeWidth="1.5" />

                {isActive ? (
                  <g filter="url(#lcdGlow)">
                    {/* Glowing green/blue background */}
                    <circle cx="35" cy="32" r="14.5" fill="#0d9488" opacity="0.2" />

                    {/* Voltmeter reading text */}
                    <text x="35" y="29.5" fill="#0ea5e9" fontSize="6.5" fontFamily="monospace" fontWeight="extrabold" textAnchor="middle">
                      {liveValue !== null && liveValue !== undefined ? `${Number(liveValue).toFixed(1)} ${unit || "kW"}` : "5.2 kW"}
                      <animate attributeName="opacity" values="1;0.6;1" dur="1.2s" repeatCount="indefinite" />
                    </text>

                    {/* Animated Sine Wave on the LCD (Thicker and scrolling) */}
                    <path d="M 22,37 Q 28.5,29 35,37 Q 41.5,45 48,37" stroke="#10b981" strokeWidth="2.5" fill="none">
                      <animate attributeName="stroke-dasharray" values="40;15;40" dur="1.5s" repeatCount="indefinite" />
                      <animate attributeName="stroke-dashoffset" values="0;-55" dur="1.5s" repeatCount="indefinite" />
                    </path>
                  </g>
                ) : (
                  // Static blank screen when inactive
                  <text x="35" y="34.5" fill="#64748b" fontSize="5.5" textAnchor="middle" fontFamily="monospace">OFF</text>
                )}

                {/* Glowing energy flow paths (DC in on left, AC out on right) */}
                {isActive && (
                  <g filter="url(#lcdGlow)">
                    {/* DC flow line */}
                    <path d="M 25,58 L 25,51 L 12,51 L 12,20" stroke="#f59e0b" strokeWidth="2.5" fill="none" strokeDasharray="5 5">
                      <animate attributeName="stroke-dashoffset" values="20;0" dur="0.8s" repeatCount="indefinite" />
                    </path>
                    {/* AC flow line */}
                    <path d="M 58,20 L 58,51 L 45,51 L 45,58" stroke="#10b981" strokeWidth="2.5" fill="none" strokeDasharray="5 5">
                      <animate attributeName="stroke-dashoffset" values="0;20" dur="0.8s" repeatCount="indefinite" />
                    </path>
                  </g>
                )}

                {/* LED Indicators (Power, Grid, Alarm) */}
                <circle cx="16" cy="48" r="1.8" fill={isActive ? "#22c55e" : "#064e3b"} filter={isActive ? "url(#lcdGlow)" : "none"}>
                  {isActive && (
                    <animate attributeName="opacity" values="1;0.4;1" dur="0.8s" repeatCount="indefinite" />
                  )}
                </circle>
                <circle cx="21" cy="48" r="1.8" fill={isActive ? "#eab308" : "#422006"} />
                <circle cx="26" cy="48" r="1.8" fill={isOffline ? "#ef4444" : "#450a0a"} />
                <text x="21" y="53" fontSize="4" fill="#64748b" textAnchor="middle">LEDs</text>

                {/* Bottom Cable conduit connections */}
                <rect x="22" y="56" width="6" height="4" fill="#64748b" />
                <rect x="42" y="56" width="6" height="4" fill="#64748b" />

                {/* Status LED on top right */}
                <circle cx="56" cy="11" r="4.5" fill={dotColor} stroke="#ffffff" strokeWidth="1" filter={isActive ? "url(#lcdGlow)" : "none"}>
                  {isActive && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Inverter housing */}
                <rect
                  x="5" y="5" width="60" height="40" rx="4"
                  fill={isActive ? `${color}10` : `${color}05`}
                  stroke={isActive ? color : "#64748b"}
                  strokeWidth="2"
                />

                {/* DC input label */}
                <text x="14" y="18" fontSize="7" fontWeight="bold" fill={color} opacity="0.6">DC</text>

                {/* DC flat line */}
                <line x1="10" y1="28" x2="30" y2="28" stroke={color} strokeWidth="1.5" opacity="0.5" />

                {/* Conversion arrow */}
                <polygon points="32,25 38,28 32,31" fill={isActive ? color : "#94a3b8"} />

                {/* AC output label */}
                <text x="50" y="18" fontSize="7" fontWeight="bold" fill={color} opacity="0.6">AC</text>

                {/* AC sine wave */}
                <path
                  d="M40 28 Q45 20 50 28 Q55 36 60 28"
                  stroke={isActive ? color : "#94a3b8"}
                  strokeWidth="1.5"
                  fill="none"
                  style={{ animation: isActive ? `live-inverter-wave ${waveDuration} linear infinite` : "none" }}
                />

                {/* Conversion pulse */}
                {isActive && (
                  <g style={{ animation: `live-electrical-pulse ${pulseDuration} ease-in-out infinite` }}>
                    <circle cx="35" cy="28" r="4" fill={color} opacity="0.15" />
                    <circle cx="35" cy="28" r="2" fill={color} opacity="0.4" />
                  </g>
                )}

                {/* Input/output connectors */}
                <rect x="0" y="24" width="5" height="8" fill={color} rx="2" />
                <rect x="65" y="24" width="5" height="8" fill={color} rx="2" />

                {/* Status dot */}
                <circle cx="60" cy="10" r="3" fill={dotColor} />
              </>
            )}
            {/* Status text - clearly below inverter body */}
            <text x="35" y="72" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
              {statusColor === "green" ? "CONVERTING" : statusColor === "red" ? "OFF" : "OFFLINE"}
            </text>
          </svg>
        );
      }}
    </BaseNode>
  );
};

SolarInverterNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default SolarInverterNode;
